import { useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { usePhAddresses } from '../../../shared/hooks/usePhAddresses';
import { GRADE_LEVELS } from '../../../shared/constant/gradeLevels';
import { STRANDS_BY_GRADE } from '../../../shared/constant/strands';
import { getSchoolYearOptions, CURRENT_SCHOOL_YEAR } from '../../../shared/constant/schoolYears';
import { PH_MOBILE_PATTERN, formatPhMobileInput, isValidPhMobile } from '../../../shared/utils/phoneNumber';
import { showSuccessToast } from '../../../shared/utils/dialogs';

const emptyForm = {
  lrn: '',
  lastname: '',
  firstname: '',
  middlename: '',
  school_year: CURRENT_SCHOOL_YEAR,
  grade_level: GRADE_LEVELS[0],
  section: '',
  strand: '',
  guardian_name: '',
  guardian_contact: '',
  province: '',
  town: '',
  brgy: '',
  status: 'Active',
};

function StudentFormModal({ student, onClose, onSubmit }) {
  const { addresses, loading: addressesLoading } = usePhAddresses();
  const [form, setForm] = useState(() => (student ? { ...emptyForm, ...student } : emptyForm));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const provinces = useMemo(
    () => [...new Set(addresses.map((a) => a.province))].sort(),
    [addresses]
  );
  const towns = useMemo(
    () =>
      form.province
        ? [...new Set(addresses.filter((a) => a.province === form.province).map((a) => a.town))].sort()
        : [],
    [addresses, form.province]
  );
  const barangays = useMemo(
    () =>
      form.province && form.town
        ? addresses.filter((a) => a.province === form.province && a.town === form.town).map((a) => a.brgy).sort()
        : [],
    [addresses, form.province, form.town]
  );

  const strandOptions = STRANDS_BY_GRADE[form.grade_level] ?? [];

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      if (field === 'province') return { ...prev, province: value, town: '', brgy: '' };
      if (field === 'town') return { ...prev, town: value, brgy: '' };
      if (field === 'grade_level') return { ...prev, grade_level: value, strand: '' };
      if (field === 'guardian_contact') return { ...prev, guardian_contact: formatPhMobileInput(value) };
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.guardian_contact && !isValidPhMobile(form.guardian_contact)) {
      setError('Guardian contact must be in the format 09XX-XXX-XXXX.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        ...form,
        lrn: form.lrn || null,
        middlename: form.middlename || null,
        section: form.section || null,
        strand: form.strand || null,
        guardian_name: form.guardian_name || null,
        guardian_contact: form.guardian_contact || null,
        province: form.province || null,
        town: form.town || null,
        brgy: form.brgy || null,
      });
      showSuccessToast(student ? 'Student updated successfully' : 'Student added successfully');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save student');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" />
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[70vh] overflow-hidden flex flex-col"
      >
        <div className="shrink-0 flex items-center justify-between px-5 py-4 bg-navy-light">
          <h2 className="text-lg font-semibold text-white">{student ? 'Edit Student' : 'Add Student'}</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-200 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <p className="text-sm text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">{error}</p>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">LRN (12 digits)</label>
            <input
              value={form.lrn}
              onChange={set('lrn')}
              pattern="[0-9]{12}"
              maxLength={12}
              title="LRN must be exactly 12 digits"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
            />
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Last Name</label>
              <input
                required
                value={form.lastname}
                onChange={set('lastname')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">First Name</label>
              <input
                required
                value={form.firstname}
                onChange={set('firstname')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Middle Name</label>
              <input
                value={form.middlename}
                onChange={set('middlename')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
          </div>
          

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Grade Level</label>
              <select
                required
                value={form.grade_level}
                onChange={set('grade_level')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              >
                {GRADE_LEVELS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Section</label>
              <input
                value={form.section}
                onChange={set('section')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">School Year</label>
              <select
                required
                value={form.school_year}
                onChange={set('school_year')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              >
                {getSchoolYearOptions().map((sy) => (
                  <option key={sy} value={sy}>
                    {sy}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select
                value={form.status}
                onChange={set('status')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {strandOptions.length > 0 && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Strand</label>
              <select
                required
                value={form.strand}
                onChange={set('strand')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              >
                <option value="">Select</option>
                {strandOptions.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Guardian Name</label>
              <input
                value={form.guardian_name}
                onChange={set('guardian_name')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Guardian Contact</label>
              <input
                type="tel"
                inputMode="numeric"
                value={form.guardian_contact}
                onChange={set('guardian_contact')}
                pattern={PH_MOBILE_PATTERN}
                maxLength={13}
                placeholder="09XX-XXX-XXXX"
                title="Guardian contact must be in the format 09XX-XXX-XXXX"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Province</label>
              <select
                disabled={addressesLoading}
                value={form.province}
                onChange={set('province')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg disabled:opacity-50"
              >
                <option value="">Select</option>
                {provinces.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Town</label>
              <select
                disabled={!form.province}
                value={form.town}
                onChange={set('town')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg disabled:opacity-50"
              >
                <option value="">Select</option>
                {towns.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Barangay</label>
              <select
                disabled={!form.town}
                value={form.brgy}
                onChange={set('brgy')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg disabled:opacity-50"
              >
                <option value="">Select</option>
                {barangays.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
          </div>

        </div>

        <div className="shrink-0 flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="w-28 py-2 text-center text-lg font-medium text-gray-600 border border-gray-300 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="w-28 py-2 rounded-md bg-navy-light text-white text-lg font-medium text-center hover:bg-navy disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default StudentFormModal;
